// ======================================================
// TEF LoginScreen — React-Port des Server-Designs (server/views/login.html)
// ======================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sanitizeHtml } from '../utils/sanitize';

// ── SVG Icons (tageszeit-basierte Begrüßung) ──────────────────────────────────
const GREETING_ICONS = {
  morning:   `<svg viewBox="0 0 24 24" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" width="18" height="18"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`,
  evening:   `<svg viewBox="0 0 24 24" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" width="18" height="18"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`,
  night:     `<svg viewBox="0 0 24 24" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" width="18" height="18"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/><circle cx="7" cy="8" r="0.5" fill="white"/><circle cx="15" cy="6" r="0.5" fill="white"/><circle cx="18" cy="11" r="0.5" fill="white"/></svg>`,
  noon:      `<svg viewBox="0 0 24 24" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" width="18" height="18"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`,
  afternoon: `<svg viewBox="0 0 24 24" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" width="18" height="18"><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/></svg>`,
};

const WEEKDAYS = ['Sonntag','Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag'];

function getGreeting(h) {
  if (h >= 3  && h < 11) return { text: 'Guten Morgen',         icon: GREETING_ICONS.morning,    isEvening: false };
  if (h >= 11 && h < 12) return { text: 'Schönen Vormittag',    icon: GREETING_ICONS.morning,    isEvening: false };
  if (h >= 12 && h < 14) return { text: 'Schönen Mittag',       icon: GREETING_ICONS.noon,       isEvening: false };
  if (h >= 14 && h < 17) return { text: 'Schönen Nachmittag',   icon: GREETING_ICONS.afternoon,  isEvening: false };
  if (h >= 17 && h < 22) return { text: 'Guten Abend',          icon: GREETING_ICONS.evening,    isEvening: true  };
  return                         { text: 'Hallo Nachtschwärmer', icon: GREETING_ICONS.night,      isEvening: true, isNight: true };
}

const LOCK_CLOSED = `<svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;"><path d="M7 10V8a5 5 0 0 1 10 0v2"/><path d="M6.5 10h11a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"/></svg>`;
const LOCK_OPEN   = `<svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M16 11V8a4 4 0 0 0-8 0"/></svg>`;

const CSS = `
  @keyframes tef-lock-success{0%{transform:scale(1) rotate(0deg)}40%{transform:scale(1.08) rotate(-6deg)}70%{transform:scale(.98) rotate(4deg)}100%{transform:scale(1) rotate(0deg)}}
  @keyframes tef-field-error-pulse{0%,100%{box-shadow:0 0 12px rgba(239,68,68,0.35)}50%{box-shadow:0 0 20px rgba(239,68,68,0.5)}}
  .tef-login-field{display:flex;align-items:center;gap:10px;padding:0 12px;border-radius:16px;border:1px solid rgba(255,255,255,.16);background:rgba(0,0,0,.2);height:48px;box-sizing:border-box;position:relative;transition:box-shadow .2s,border-color .2s}
  .tef-login-field:focus-within{box-shadow:0 0 0 4px rgba(80,140,255,.25);border-color:#6fa8ff}
  .tef-login-field.error{border-color:rgba(255,100,100,.6)!important;box-shadow:0 0 12px rgba(239,68,68,0.35),0 0 4px rgba(255,100,100,0.2)!important;animation:tef-field-error-pulse .6s ease}
  .tef-login-field input{flex:1;min-width:0;background:transparent;border:0;outline:none;color:#eaf0ff;font-size:14px;height:100%;font-family:inherit}
  .tef-login-field input::placeholder{color:rgba(234,240,255,.45);font-size:14px}
  .tef-login-field input.filled{color:#7fd8ff!important}
  .tef-login-field input:-webkit-autofill { -webkit-text-fill-color: #7fd8ff !important; transition: background-color 5000s ease-in-out 0s; }
  .tef-login-field input:autofill { -webkit-text-fill-color: #7fd8ff !important; transition: background-color 5000s ease-in-out 0s; }
  .tef-login-btn{margin-top:18px;width:100%;padding:14px;border-radius:16px;border:0;font-weight:700;color:white;cursor:pointer;background:linear-gradient(135deg,#0a48ff,#6a00ff);box-shadow:0 0 28px rgba(10,72,255,.5);transition:all .2s;display:inline-flex;align-items:center;justify-content:center;gap:10px;font-size:15px;font-family:inherit}
  .tef-login-btn:hover{transform:translateY(-1px)}
  .tef-login-btn:active{transform:translateY(0)}
  .tef-login-btn.success{background:linear-gradient(135deg,#16a34a,#10b981)!important;box-shadow:0 6px 30px rgba(16,185,129,0.25)!important}
  .tef-login-btn.fail{background:linear-gradient(135deg,#ef4444,#f97316)!important;box-shadow:0 6px 30px rgba(239,68,68,0.2)!important}
  .tef-login-btn:disabled{opacity:.7;cursor:not-allowed}
  .tef-lock-icon{width:22px;height:22px;display:inline-grid;place-items:center;}
  .tef-lock-icon.animate{animation:tef-lock-success 800ms cubic-bezier(.2,.9,.3,1)}
  .tef-pw-toggle{flex-shrink:0;background:transparent;border:0;color:rgba(234,240,255,.7);cursor:pointer;width:20px;height:20px;display:grid;place-items:center;padding:0}
  .tef-pw-toggle svg{width:14px;height:14px;stroke:rgba(234,240,255,.7);stroke-width:1.5}
  .tef-pw-toggle:hover svg,.tef-pw-toggle.active svg{stroke:#7fd8ff;filter:drop-shadow(0 0 6px rgba(127,216,255,0.2))}
  .tef-login-link{color:#00e5ff;text-shadow:0 0 8px rgba(0,229,255,0.4);text-decoration:none;font-size:12px;cursor:pointer;background:none;border:0;padding:0;font-family:inherit}
  .tef-login-link:hover{color:#ff4dff!important;text-shadow:0 0 10px rgba(255,77,255,0.5)!important}
`;

let cssInjected = false;
function ensureCSS() {
  if (cssInjected) return;
  cssInjected = true;
  const s = document.createElement('style');
  s.textContent = CSS;
  document.head.appendChild(s);
}

// ── Helper-Komponente für den Lockout-Timer ──────────────────────────────
function LockoutTimer({ endTime, onExpire }) {
  const [timeLeft, setTimeLeft] = useState(Math.max(0, endTime - Date.now()));

  useEffect(() => {
    if (timeLeft <= 0) {
      if (onExpire) onExpire();
      return;
    }
    const id = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0 && onExpire) {
        clearInterval(id);
        onExpire();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [endTime, onExpire, timeLeft]);

  const m = String(Math.floor(timeLeft / 60000)).padStart(2, '0');
  const s = String(Math.floor((timeLeft % 60000) / 1000)).padStart(2, '0');
  
  return <span>{m}:{s}</span>;
}

function ClockAndGreeting() {
  const [clock, setClock] = useState('--:--:--');
  const [greeting, setGreeting] = useState(getGreeting(new Date().getHours()));

  useEffect(() => {
    function tick() {
      const now = new Date();
      const h = now.getHours();
      const m = String(now.getMinutes()).padStart(2,'0');
      const sc = String(now.getSeconds()).padStart(2,'0');
      setClock(`${String(h).padStart(2,'0')}:${m}:${sc}`);
      setGreeting(getGreeting(h));
    }
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  const s = {
    clockWrap: { display:'flex', flexDirection:'row', alignItems:'center', gap:'10px' },
    clockIcon: { width:'30px', height:'30px', display:'grid', placeItems:'center' },
    clockText: { fontSize:'14px', color:'#7fd8ff', textShadow:'0 0 6px rgba(127,216,255,0.25)', fontWeight:400 },
    greetingWrap: { textAlign:'right', minWidth:'160px' },
    greetingBar: { display:'flex', alignItems:'center', justifyContent:'flex-end', gap:'8px', marginBottom:'6px' },
    greetingIcon: { width:'30px', height:'30px', borderRadius:'8px', display:'grid', placeItems:'center', background:'linear-gradient(135deg,#0a48ff,#6a00ff)', boxShadow:'0 6px 18px rgba(10,72,255,0.18)' },
    greetingText: { fontSize:'14px', fontWeight:700, color: greeting.isEvening ? '#7fd8ff' : '#eaf0ff', textShadow: greeting.isEvening ? '0 0 8px rgba(127,216,255,0.25)' : 'none', lineHeight:1 },
    dateCombined: { fontSize:'11px', color:'rgba(234,240,255,.7)' },
  };

  const now = new Date();
  const d  = String(now.getDate()).padStart(2,'0');
  const mo = String(now.getMonth()+1).padStart(2,'0');
  const y  = now.getFullYear();
  const wd = WEEKDAYS[now.getDay()];

  return (
    <>
      <div style={s.clockWrap}>
        <div style={s.clockIcon}>
          <svg viewBox="0 0 24 24" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" width="18" height="18">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div style={s.clockText}>{clock}</div>
      </div>
      <div style={s.greetingWrap}>
        <div style={s.greetingBar}>
          <div style={s.greetingIcon} dangerouslySetInnerHTML={{ __html: sanitizeHtml(greeting.icon) }} />
          {greeting.isNight
            ? <div style={s.greetingText}><span style={{ display:'flex', flexDirection:'column', alignItems:'flex-start' }}><span>Hallo</span><span style={{ fontSize:'12px', fontWeight:600, opacity:.9 }}>Nachtschwärmer</span></span></div>
            : <div style={s.greetingText}>{greeting.text}</div>
          }
        </div>
        <div style={s.dateCombined}>{wd} · {d}.{mo}.{y}</div>
      </div>
    </>
  );
}

// ── Haupt-Komponente ───────────────────────────────────────────────────────────
export function LoginScreen({ onLogin, onLogin2FA, error: parentError }) {
  ensureCSS();

  const [username, setUsername]       = useState('');
  const [password, setPassword]       = useState('');
  const [showPw, setShowPw]           = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [btnState, setBtnState]       = useState('');
  const [lockHtml, setLockHtml]       = useState(LOCK_CLOSED);
  const [animateLock, setAnimateLock] = useState(false);
  const [showForgot, setShowForgot]   = useState(false);
  const [localError, setLocalError]   = useState(null);
  
  const error = localError || parentError;
  
  // 2FA States
  const [step, setStep] = useState('login'); // 'login', '2fa'
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  
  const [lockoutState, setLockoutState] = useState(() => {
    try {
      const stored = localStorage.getItem('tef_lockout');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.permanent) return parsed;
        if (parsed.endTime && parsed.endTime > Date.now()) return parsed;
        localStorage.removeItem('tef_lockout');
      }
    } catch (e) {}
    return null;
  }); // { active: boolean, permanent: boolean, endTime: number|null }

  useEffect(() => {
    if (lockoutState && lockoutState.active) {
      localStorage.setItem('tef_lockout', JSON.stringify(lockoutState));
    } else {
      localStorage.removeItem('tef_lockout');
    }
  }, [lockoutState]);

  const usernameRef = useRef(null);

  useEffect(() => { usernameRef.current?.focus(); }, []);

  const now = new Date();
  const d  = String(now.getDate()).padStart(2,'0');
  const mo = String(now.getMonth()+1).padStart(2,'0');
  const y  = now.getFullYear();
  const bottomDateStr = `${WEEKDAYS[now.getDay()]} | ${d}.${mo}.${y}`;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim() || submitting) return;
    setSubmitting(true);
    setBtnState('');
    setLocalError(null);
    const result = await onLogin(username.trim(), password);
    
    if (result && result.require2FA) {
      setTempToken(result.tempToken);
      setStep('2fa');
      setSubmitting(false);
      return;
    }
    
    if (result && result.ok) {
      setLockHtml(LOCK_OPEN);
      setAnimateLock(true);
      setBtnState('success');
      setTimeout(() => setAnimateLock(false), 900);
    } else {
      if (result && result.locked) {
        if (result.permanent) {
          setLockoutState({ active: true, permanent: true, endTime: null });
        } else {
          setLockoutState({ active: true, permanent: false, endTime: Date.now() + (result.minsLeft * 60 * 1000) });
        }
      } else if (result && result.remaining !== undefined) {
        setLocalError(`Zugriff verweigert. Noch ${result.remaining} Versuch(e) übrig.`);
      }
      setBtnState('fail');
      setTimeout(() => setBtnState(''), 900);
    }
    setSubmitting(false);
  }, [username, password, submitting, onLogin]);

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!totpCode.trim() || submitting || !onLogin2FA) return;
    setSubmitting(true);
    setBtnState('');
    setLocalError(null);
    
    const result = await onLogin2FA(tempToken, totpCode.replace(/\s+/g, ''));
    if (result && result.ok) {
      setLockHtml(LOCK_OPEN);
      setAnimateLock(true);
      setBtnState('success');
      setTimeout(() => setAnimateLock(false), 900);
    } else {
      setLocalError(result?.message || 'Ungültiger Code');
      setBtnState('fail');
      setTimeout(() => setBtnState(''), 900);
    }
    setSubmitting(false);
  };

  const s = {
    wrap: { flex: 1, width: '100%', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:`radial-gradient(900px 500px at 50% 0%,rgba(10,72,255,.5),transparent 60%),radial-gradient(700px 500px at 10% 90%,rgba(106,0,255,.45),transparent 60%),linear-gradient(135deg,#030b2e,#070033)`, color:'#eaf0ff', fontFamily:"system-ui,-apple-system,'Segoe UI',Roboto,Arial,sans-serif" },
    card: { width:'420px', maxWidth:'96vw', padding:'28px 26px', borderRadius:'26px', background:'rgba(255,255,255,0.06)', backdropFilter:'blur(22px) saturate(120%)', WebkitBackdropFilter:'blur(22px) saturate(120%)', border:'1px solid rgba(255,255,255,0.08)', boxShadow:'0 40px 90px rgba(0,0,0,.5),0 6px 30px rgba(10,48,255,0.08)', position:'relative', overflow:'hidden' },
    topline: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', marginBottom:'18px' },
    header: { display:'flex', alignItems:'flex-end', gap:'14px', marginBottom:'20px' },
    iconBox: { width:'44px', height:'44px', borderRadius:'16px', display:'grid', placeItems:'center', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.16)', boxShadow:'0 0 24px rgba(120,100,255,.4)', flexShrink:0 },
    h1: { margin:0, fontSize:'26px', fontWeight:700 },
    subtitle: { margin:'6px 0 0', fontSize:'12px', color:'rgba(234,240,255,.7)' },
    errBox: { marginBottom:'14px', padding:'10px 14px', borderRadius:'12px', background:'rgba(255,60,60,.15)', border:'1px solid rgba(255,100,100,.3)', color:'#ff9090', fontSize:'13px' },
    label: { display:'block', margin:'16px 0 6px', fontSize:'12px', color:'rgba(234,240,255,.7)' },
    row: { display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'14px', fontSize:'12px', color:'rgba(234,240,255,.7)' },
    footer: { marginTop:'14px', textAlign:'center', fontSize:'11px', color:'rgba(234,240,255,.55)' },
    bottomDate: { marginTop:'8px', textAlign:'center', fontSize:'11px', color:'#00e5ff', textShadow:'0 0 8px rgba(0,229,255,0.35),0 0 20px rgba(0,229,255,0.18)', fontWeight:600 },
  };

  return (
    <div style={s.wrap}>
      <div style={s.card}>

        {/* ── Top-Line: Uhr | Begrüßung ─────────────────── */}
        <div style={s.topline}>
          <ClockAndGreeting />
        </div>

        {!showForgot ? (
          <>
            {/* ── Header ──────────────────────────────── */}
            <div style={s.header}>
              <div style={s.iconBox}>
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" width="22" height="22">
                  <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
                </svg>
              </div>
              <div>
                <h1 style={s.h1}>TEF Dashboard</h1>
                <p style={s.subtitle}>Promoter Console · Anmelden</p>
              </div>
            </div>

            {/* ── Fehler-Box ────────────────────────── */}
            {error && (
              <div style={s.errBox}>
                <span style={{ fontWeight: 600 }}>{error}</span>
                <div style={{ marginTop:'6px', fontSize:'11px', opacity:.85, lineHeight:1.5 }}>
                  <div style={{ opacity:.75 }}>Sicherheits-Lockout nach 5 Fehlversuchen:</div>
                  <div style={{ opacity:.65 }}>15 Min → 30 Min → 60 Min → Dauerhaft</div>
                </div>
              </div>
            )}

            {/* ── Formular ──────────────────────────── */}
            {step === '2fa' ? (
              <form onSubmit={handle2FASubmit} noValidate>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '25px', marginTop: '10px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(10, 72, 255, 0.15)', display: 'grid', placeItems: 'center', marginBottom: '15px', border: '1px solid rgba(10, 72, 255, 0.3)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0110 0v4"></path>
                    </svg>
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px', color: '#fff' }}>2-Faktor-Authentifizierung</h2>
                  <p style={{ color: 'rgba(234,240,255,.7)', fontSize: '11px', textAlign: 'center', margin: 0, lineHeight: 1.4 }}>
                    Bitte gib den 6-stelligen Code aus deiner<br/>Authenticator-App ein.
                  </p>
                </div>

                <div className="tef-login-field" style={{ marginBottom: '25px', display: 'flex', justifyContent: 'center' }}>
                  <input 
                    type="text" 
                    value={totpCode} 
                    onChange={e => setTotpCode(e.target.value.replace(/[^0-9]/g, ''))}
                    className={totpCode ? 'filled' : ''} 
                    placeholder="000 000" 
                    disabled={submitting} 
                    style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px', fontWeight: '700', paddingLeft: '24px' }}
                    maxLength={6}
                    autoFocus 
                  />
                </div>
                
                <button type="submit" className={`tef-login-btn${btnState ? ' '+btnState : ''}`}
                  disabled={submitting || totpCode.length < 6}>
                  <span className={`tef-lock-icon${animateLock ? ' animate' : ''}`} dangerouslySetInnerHTML={{ __html: lockHtml }} />
                  <span>{submitting ? 'Prüfen...' : 'Code bestätigen'}</span>
                </button>
                
                <button type="button" className="tef-login-link" onClick={() => { setStep('login'); setTotpCode(''); setLocalError(null); }} style={{ width: '100%', textAlign: 'center', marginTop: '20px' }}>
                  Zurück zum Login
                </button>
              </form>
            ) : lockoutState && lockoutState.active ? (
              <div style={{ textAlign: 'center', padding: '30px 10px', animation: 'tef-fade-in 0.5s ease-out' }}>
                <div style={{ position: 'relative', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', marginBottom: '24px' }}>
                  {/* Pulsierender Hintergrund-Kreis */}
                  <div style={{ position: 'absolute', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.1)', animation: 'tef-field-error-pulse 2s infinite' }}></div>
                  
                  {/* SIM Karten / Chip Icon im O2 Style */}
                  <div style={{ position: 'relative', background: 'linear-gradient(135deg, #0a1929 0%, #0019a5 100%)', width: '72px', height: '96px', borderRadius: '8px 24px 8px 8px', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 8px 32px rgba(0, 25, 165, 0.4)', display: 'flex', flexDirection: 'column', padding: '12px', boxSizing: 'border-box' }}>
                    <div style={{ width: '100%', height: '100%', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr 1fr', gap: '4px', padding: '4px' }}>
                      <div style={{ border: '1px solid rgba(255,255,255,0.2)', borderLeft: 'none', borderTop: 'none' }}></div>
                      <div style={{ border: '1px solid rgba(255,255,255,0.2)', borderRight: 'none', borderTop: 'none' }}></div>
                      <div style={{ border: '1px solid rgba(255,255,255,0.2)', borderLeft: 'none' }}></div>
                      <div style={{ border: '1px solid rgba(255,255,255,0.2)', borderRight: 'none' }}></div>
                      <div style={{ border: '1px solid rgba(255,255,255,0.2)', borderLeft: 'none', borderBottom: 'none' }}></div>
                      <div style={{ border: '1px solid rgba(255,255,255,0.2)', borderRight: 'none', borderBottom: 'none' }}></div>
                    </div>
                    {/* Kleines rotes Warn-Badge */}
                    <div style={{ position: 'absolute', bottom: '-8px', right: '-8px', width: '28px', height: '28px', background: '#ef4444', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.6)', border: '2px solid #000' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                        <path d="M18 6L6 18M6 6l12 12"></path>
                      </svg>
                    </div>
                  </div>
                </div>
                
                <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#ef4444', margin: '0 0 8px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  System-Schutz aktiv
                </h2>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '20px' }}>
                  Netzwerkzugang verweigert
                </div>
                
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '15px', lineHeight: 1.6, margin: '0 auto 24px', maxWidth: '300px' }}>
                  {lockoutState.permanent 
                    ? "Sicherheitssperre: Dieser Zugang wurde aufgrund wiederholter Fehlversuche dauerhaft vom Netzwerk getrennt. Bitte kontaktiere den IT-Support." 
                    : "Zum Schutz vor dem Zugriff durch Dritte und Unbefugte wurde die Verbindung vorübergehend getrennt, da keine gültige Autorisierung erfolgt ist."}
                </p>
                
                {!lockoutState.permanent && (
                  <div style={{ background: 'rgba(0,0,0,0.4)', padding: '16px', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.2)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#ef4444' }}></div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>
                      Verbindungswiederherstellung in
                    </div>
                    <div style={{ fontSize: '36px', fontWeight: 900, color: '#fff', fontFamily: 'monospace', letterSpacing: '2px', textShadow: '0 0 16px rgba(255,255,255,0.2)' }}>
                      <LockoutTimer endTime={lockoutState.endTime} onExpire={() => setLockoutState(null)} />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <label style={s.label}>Benutzername</label>
                <div className="tef-login-field">
                  <input ref={usernameRef} type="text" value={username} onChange={e => setUsername(e.target.value)}
                    className={username ? 'filled' : ''} placeholder="Dein Benutzername" autoComplete="username" disabled={submitting} />
                </div>
  
                <label style={s.label}>Passwort</label>
                <div className="tef-login-field">
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    className={password ? 'filled' : ''} placeholder="••••••••" autoComplete="current-password" disabled={submitting} />
                  <button type="button" className={`tef-pw-toggle${showPw ? ' active' : ''}`} onClick={() => setShowPw(!showPw)} aria-label="Passwort anzeigen">
                    {showPw
                      ? <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                      : <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94C16.01 19.13 13.57 20 12 20 5 20 1 12 1 12c1.21-2.06 3.09-3.8 5.29-5.06"/><path d="M22.54 11.54C21.95 10.88 20.9 9.58 19.21 8.33"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    }
                  </button>
                </div>
  
                <div style={s.row}>
                  <label style={{ display:'inline-flex', alignItems:'center', gap:'6px', margin:0, cursor:'pointer' }}>
                    <input type="checkbox" style={{ accentColor:'#0a48ff' }} />
                    <span>Angemeldet bleiben</span>
                  </label>
                  <button type="button" className="tef-login-link" onClick={() => setShowForgot(true)}>
                    Passwort vergessen?
                  </button>
                </div>
  
                <button type="submit" className={`tef-login-btn${btnState ? ' '+btnState : ''}`}
                  disabled={submitting || !username.trim() || !password.trim()}>
                  <span className={`tef-lock-icon${animateLock ? ' animate' : ''}`} dangerouslySetInnerHTML={{ __html: lockHtml }} />
                  <span>{submitting ? 'Anmelden...' : 'Login'}</span>
                </button>
              </form>
            )}
          </>
        ) : (
          /* ── Passwort vergessen Panel ─────────────── */
          <div>
            <h2 style={{ margin:'0 0 8px', fontSize:'18px', fontWeight:700 }}>Passwort zurücksetzen</h2>
            <p style={{ margin:'0 0 12px', color:'rgba(234,240,255,.7)', fontSize:'10px', lineHeight:1.5 }}>
              Gib Benutzernamen und E‑Mail ein.<br />
              Wir senden einen Link, falls ein Konto existiert.<br />
              <span style={{ color:'#ff9090' }}>Beide Felder sind Pflichtfelder.</span>
            </p>
            <form onSubmit={e => { e.preventDefault(); }} noValidate>
              <label style={s.label}>Benutzername</label>
              <div className="tef-login-field"><input type="text" placeholder="Dein Benutzername" autoComplete="username" /></div>
              <label style={s.label}>E‑Mail</label>
              <div className="tef-login-field"><input type="email" placeholder="E‑Mail Adresse" autoComplete="email" /></div>
              <button type="submit" className="tef-login-btn">Link senden</button>
            </form>
            <div style={{ marginTop:'12px', fontSize:'13px' }}>
              <button type="button" className="tef-login-link" onClick={() => setShowForgot(false)}>← Zurück zur Anmeldung</button>
            </div>
          </div>
        )}

        <div style={s.footer}>Nur für berechtigte Nutzer</div>
        <div style={s.bottomDate}>{bottomDateStr}</div>
      </div>
    </div>
  );
}
